<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Buyer information"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasBuyerData"
            :has-errors="hasSectionErrors"
          />
          Buyer information
          <feather-icon
            v-b-tooltip.hover.top="'If the buyer and the importer are the same, it is not necessary to provide the buyer\'s information.'"
            icon="InfoIcon"
            size="14"
            class="ml-50 text-muted"
          />
        </span>
      </template>

      <validation-observer>
        <!-- Buyer Section -->
        <b-row v-if="loading">
          <b-col class="text-center">
            <b-spinner label="Loading..." />
            <h6>
              Retrieving buyer data...
            </h6>
          </b-col>
        </b-row>

        <b-row v-if="buyer && localLoading === false">
          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Buyer name"

            >
              <label>Buyer name</label>
              <b-form-input
                v-model="buyer.name"
                v-b-tooltip.hover.top="getIssueMessage('buyer.name')"
                :class="getIssueClass('buyer.name')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Buyer registry number"

            >
              <label>Buyer registry number</label>
              <b-form-input
                v-model="buyer.registry_number"
                v-b-tooltip.hover.top="getIssueMessage('buyer.registry_number')"
                :class="getIssueClass('buyer.registry_number')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Buyer street number"

            >
              <label>Buyer street number</label>
              <b-form-input
                v-model="buyer.street_and_number"
                v-b-tooltip.hover.top="getIssueMessage('buyer.street_and_number')"
                :class="getIssueClass('buyer.street_and_number')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Buyer postal code"
              rules="max:20"
            >
              <label>Buyer postal code</label>
              <b-form-input
                v-model="buyer.postal_code"
                v-b-tooltip.hover.top="getIssueMessage('buyer.postal_code')"
                :class="getIssueClass('buyer.postal_code')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Buyer city"

            >
              <label>Buyer city</label>
              <b-form-input
                v-model="buyer.city"
                v-b-tooltip.hover.top="getIssueMessage('buyer.city')"
                :class="getIssueClass('buyer.city')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <label>Buyer region</label>
            <b-form-input
              v-model="buyer.region"
              v-b-tooltip.hover.top="getIssueMessage('buyer.region')"
              :class="getIssueClass('buyer.region')"
              :disabled="disabled"
              class="mr-0 ml-0"
            />
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Buyer country"
              rules="min:2|max:2|alpha"
            >
              <label>Buyer country</label>
              <b-form-input
                v-model="buyer.country"
                v-b-tooltip.hover.top="getIssueMessage('buyer.country')"
                :class="getIssueClass('buyer.country')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormInput,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'
import { EventBus } from '@core/utils/utils'

export default {
  components: {
    BRow,
    BCol,
    BFormInput,
    BSpinner,
    ValidationObserver,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: Number,
      required: true,
    },
    shipmentId: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      buyer: {},
      localLoading: this.loading,
    }
  },
  computed: {
    hasBuyerData() {
      if (!this.buyer) {
        return false
      }
      return !!(this.buyer.name || this.buyer.registry_number || this.buyer.street_and_number || this.buyer.postal_code || this.buyer.city || this.buyer.region || this.buyer.country)
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('buyer.'))
    },
  },
  watch: {
    loading(newVal) {
      this.localLoading = newVal
    },
  },
  created() {
    this.fetchBuyerData()
    EventBus.$on('update-buyer', this.handleUpdateBuyerData)
    EventBus.$on('buyer-data-loader', this.handleBuyerDataLoader)
  },
  beforeDestroy() {
    EventBus.$off('buyer-data-loader', this.handleBuyerDataLoader)
    EventBus.$off('update-buyer', this.handleUpdateBuyerData)
  },
  methods: {
    fetchBuyerData() {
      this.$http.get(`/v1/companies/${this.companyId}/shipments/${this.shipmentId}/parcels/${this.parcel.id}/buyer-data`)
        .then(response => {
          this.buyer = response.data.buyer
          this.localLoading = false
          EventBus.$emit('update-buyer', this.buyer)
          EventBus.$emit('buyer-data-loader', false)
        })
        .catch(() => {
          this.localLoading = false
          EventBus.$emit('buyer-data-loader', false)
        })
    },
    handleBuyerDataLoader(loader) {
      this.localLoading = loader
    },
    handleUpdateBuyerData(newBuyer) {
      this.buyer = newBuyer
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
